import { cva } from 'class-variance-authority';

/**
 * Menu class recipes — the action menu ("more actions"), not the select's option list.
 *
 * Zero `dark:` variants, as everywhere else in this layer: colour comes entirely from the
 * `--ds-menu-*` slots, which `.ds-menu` sets per theme in `components/styles/design-system.css`.
 * Read the block header there first — three of those slots are EXTRAPOLATED rather than authored,
 * because the drawn `Menu` component is a selection list with no divider and no destructive row.
 *
 * Geometry is measured off node 556:20625 and matches the select's list exactly, which is expected:
 * Figma draws one `Menu` component and both surfaces instance it.
 */

/**
 * The panel.
 *
 * `ds-menu` is applied by the component rather than inherited, for the same reason `Select` applies
 * `ds-select`: the popup is portalled into `<body>`, so it has none of the trigger's ancestors and
 * would otherwise resolve every slot to its light-mode fallback. The theme class goes on the
 * `Portal` instead, which IS an ancestor of this element, so `:where(.dark) .ds-menu` still reaches
 * it.
 *
 * Width is content-driven with a 220px floor, and this is the one place the action menu must NOT
 * copy the select. The select pins its panel to `--anchor-width + 2px` so the list lines up with
 * the field it belongs to; an action menu's anchor is a 40px icon button, so borrowing that rule
 * would render a 40px-wide panel. 220px is the row width the component draws (node 556:20713), used
 * here as a minimum rather than a fixed size so a long label sets the width instead of truncating.
 *
 * `max-h-[--available-height]` plus scrolling keeps a long menu on screen near the bottom of the
 * viewport; Base UI computes the value from the collision boundary.
 */
export const menuPanelRecipe = cva([
  'ds-menu',
  'box-border flex w-max min-w-[220px] flex-col',
  'max-h-[var(--available-height)] overflow-y-auto overscroll-contain',
  'px-[12px] py-[16px]',
  'rounded-[20px]',
  'border-solid border-[length:1px] border-[var(--ds-menu-border)]',
  'bg-[var(--ds-menu-bg)]',
  'outline-none',
]);

/**
 * The positioner.
 *
 * Only a stacking context — all the geometry is inline styles from Base UI's anchor positioning.
 * The z-index belongs HERE rather than on the panel, because this is the element Base UI gives
 * `position: fixed`; on the panel it would have nothing to stack against.
 *
 * `z-[1400]` sits above MUI's modal (1300) and drawer (1200) and below its tooltip (1500) — the
 * same value and the same reasoning as `selectPositionerRecipe`. An action menu inside a legacy MUI
 * `Dialog` is an ordinary case during the refresh, and a popup trapped behind its own dialog is
 * unusable. The value is MUI's scale rather than this layer's because MUI is what it coexists with.
 */
export const menuPositionerRecipe = cva(['z-[1400]']);

/**
 * A row.
 *
 * Figma `Label/lg` — 14px Medium on a 20px line — with 6px of inset, which is the design's 32px
 * row. Verified against the live `Menu` set on 2026-09-14: its `List item` node carries `Label/lg`,
 * and every 13px string on that page sits inside `.Menu_OLD`. The select's options use the same
 * cell.
 *
 * `data-highlighted` covers BOTH mouse hover and keyboard navigation, because Base UI highlights on
 * hover by default. That is what the design wants: it draws one hover treatment and no separate
 * keyboard-focus row, so pointer and keyboard users see the same thing and there is no second state
 * to keep in step.
 *
 * There is deliberately no selected state. The drawn component has one — it is a selection list —
 * but an action menu has nothing to select: each row runs a command and the panel closes.
 *
 * The RADIUS is an axis rather than a constant, because a described row is not a taller version of a
 * plain one — see `multiline` below.
 *
 * Selector precedence is kept mutually exclusive rather than order-dependent, as in the sibling
 * recipes: disabled wins over hovered by excluding it, so which rule applies never depends on
 * utility sort order.
 */
export const menuItemRecipe = cva(
  [
    'group/mi',
    'relative flex w-full shrink-0 items-center gap-[8px]',
    'm-0 appearance-none border-0 bg-transparent text-left',
    'px-[12px] py-[6px]',
    'font-sans text-label-lg select-none',
    'outline-none',
    'cursor-pointer',
    'transition-[color,background-color] duration-150 motion-reduce:transition-none',
    /**
     * `outline-solid` is load-bearing, and its absence made this ring invisible.
     *
     * The base above sets `outline-none` to suppress the UA ring on pointer focus. In Tailwind v4
     * that writes `--tw-outline-style: none`, and `outline-2` sets the WIDTH while reading the
     * style back from that variable — so a 2px outline was drawn in style `none`, which paints
     * nothing. Restoring the style on the focused state is what makes the slot render at all.
     *
     * Found by asserting the computed outline on a real keyboard-focused row rather than on a
     * posed one; see `KeyboardFocusRing`. The same pairing exists on `datePickerPopupRecipe`,
     * which is outside this change.
     */
    'focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--ds-menu-focus)]',

    /**
     * No disabled cell is drawn for this component, so the treatment follows the select's rather
     * than being invented separately — a dimming over the row's own colours. Documented because
     * the legacy menu DOES support disabled rows, so this is reachable.
     *
     * The dimming reads a SLOT rather than spelling a value, so a stylesheet reader can see it. As
     * an `opacity-50` literal it was invisible to the contrast harness, which meant the disabled
     * row — and the destructive-and-disabled row in particular — passed by being unobservable.
     * Dimming the row's own colours rather than recolouring it is what keeps one value correct for
     * a default row and a destructive one at once.
     */
    'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-[var(--ds-menu-disabled-opacity)]',
  ],
  {
    variants: {
      /**
       * The row's corner radius, which depends on whether it has a description.
       *
       * Design feedback on HNS-364. Both values come from the same rule — the hover fill is a shape
       * NESTED inside the panel, so its radius is the panel's radius minus the gap between the two:
       * `menuPanelRecipe` is `rounded-[20px]` with `px-[12px]`, giving 8px. A single-line row keeps
       * 24px instead because at 32px tall that clamps to a pill, which is what the design draws and
       * what the concentric rule would produce anyway if the row were as tall as the gap allows.
       *
       * A described row is ~56px (see `menuItemDescriptionRecipe`), so 24px no longer reads as a
       * pill there — it reads as a rounded rectangle whose corners are visibly tighter than the
       * panel's. 8px is the value that makes the two curves concentric.
       *
       * An axis rather than a `[&:has([data-part=item-description])]` selector: `Menu` already knows
       * which rows have descriptions, and a CSS-only version would silently do nothing on any
       * browser without `:has`.
       */
      multiline: {
        true: 'rounded-[8px]',
        false: 'rounded-[24px]',
      },
      tone: {
        default: [
          'text-[var(--ds-menu-item-fg)]',
          'data-[highlighted]:not-data-[disabled]:bg-[var(--ds-menu-item-bg-hover)]',
          'data-[highlighted]:not-data-[disabled]:text-[var(--ds-menu-item-fg-hover)]',
        ],
        /**
         * The destructive row keeps ONE label colour across resting and hovered, unlike a default
         * row's one-step brightening. The FILL is what signals the highlight here: a row tinted red
         * is unmistakably the highlighted one, so moving the label as well buys nothing and makes
         * the single destructive item on a panel read as two different reds as the cursor passes.
         */
        destructive: [
          'text-[var(--ds-menu-item-fg-destructive)]',
          'data-[highlighted]:not-data-[disabled]:bg-[var(--ds-menu-item-bg-destructive-hover)]',
        ],
      },
    },
    defaultVariants: { multiline: false, tone: 'default' },
  }
);

/**
 * A row's leading glyph. Sized and boxed exactly as the select's option icons, so a 16px icon lands
 * the same way in both menus.
 *
 * Takes the row's colour through `currentColor` rather than a slot of its own — which is what makes
 * the glyph on a destructive row red without the caller knowing anything about tones. The select's
 * icons do the same.
 */
/**
 * Sizes the row's leading glyph.
 *
 * TWO rules, because there are two kinds of glyph here and neither rule reaches both — the same
 * split `buttonIconRecipe` needs (PR #1130), in the Menu's own copy of the defect.
 *
 *   - `[&_svg]:size-full` sizes a RAW svg, which has no font-size relationship to inherit and is
 *     sized by its own `width`/`height` attributes instead.
 *   - `text-[length:16px]` sizes a MUI-backed glyph, i.e. anything out of `icons/`. `SvgIcon` draws
 *     itself at `1em` and the local-svg factory defaults `fontSize="inherit"`, so giving the SLOT a
 *     font-size is what makes that `1em` resolve to 16px.
 *
 * The font-size is the one doing the work. `size-full` cannot size a MUI glyph: it is a Tailwind
 * utility and so sits in `@layer utilities`, while `SvgIcon`'s `1em` is UNLAYERED Emotion — and
 * unlayered beats layered whatever the specificity. Without it, a row icon resolved `1em` against
 * the row's own label type size and drew smaller than the 16px box it sits in.
 *
 * A caller's own `fontSize` / `sx` on the glyph still wins, because a direct declaration beats an
 * inherited one. That is deliberate, and it is why this is safe to change in one place: every call
 * site that pins its glyph keeps drawing exactly what it draws today.
 */
export const menuItemIconRecipe = cva([
  'inline-flex size-[16px] shrink-0 items-center justify-center',
  'text-[length:16px]',
  '[&_svg]:size-full',
]);

/** The label. `min-w-0` releases the flex item's minimum content size so `truncate` can shrink it. */
export const menuItemLabelRecipe = cva(['min-w-0 truncate']);

/**
 * The column holding the label and, when there is one, the description.
 *
 * Always rendered, even for a single-line row, so the label's box is the same element in both cases
 * and a row cannot pick up different flex behaviour by gaining a description. On a row without one
 * the column collapses to the label's own line box, which is what the 32px cell already was.
 */
export const menuItemTextRecipe = cva(['flex min-w-0 flex-1 flex-col']);

/**
 * The description.
 *
 * EXTRAPOLATED — see `MenuOption.description`. Colour comes from the row through inheritance rather
 * than from a slot of its own, following `Alert`, where the title and the description are both
 * `--ds-alert-fg`.
 *
 * `text-body-md` is the scale's default reading size, chosen by what this line IS — a description —
 * rather than by matching the size it used to draw. It used to be a step smaller than the row's
 * label, on the reasoning that `Alert` separates its two lines by size and weight. The label is a
 * label style at the same size, so the two lines now differ by WEIGHT alone, which is the other
 * half of what `Alert` does and is what the library specifies for this line.
 *
 * The line box is unchanged, so a single-line description does not move the row. A description long
 * enough to WRAP is a different matter: it wraps inside the cap below, so a larger size can push it
 * onto another line and grow the row. That is accepted rather than worked around — nothing pins this
 * row's height, and the story that measures it asserts a floor.
 *
 * `max-w-[280px]` is what makes the line WRAP. The panel is `w-max`, so without a concrete width
 * the description's max-content width would simply widen the panel until the whole sentence fit on
 * one line — a ~70-character line producing a ~450px popover. 280px is a chosen number, not a
 * measured one: the drawn row is 220px (node 556:20713), and this is the next step up that reads as
 * a menu rather than a dialog. It applies here rather than on the panel so a menu WITHOUT
 * descriptions keeps sizing to its content exactly as before.
 */
export const menuItemDescriptionRecipe = cva(['text-body-md', 'max-w-[280px] whitespace-normal']);

/**
 * The group separator.
 *
 * EXTRAPOLATED — see the `.ds-menu` block header. Rendered as a bordered `<div>` rather than an
 * `<hr>`: Base UI's `Menu.Separator` sets `role="separator"` itself, and a UA-styled `<hr>` would
 * bring its own margins into a panel whose spacing is already specified.
 *
 * The negative inset is what makes the line span the full panel rather than stopping inside the
 * rows' 12px padding — the legacy menu uses MUI's `variant="middle"` Divider, which insets it. The
 * design draws no separator at all, so this follows the panel's own border, which runs edge to edge.
 * `my-[8px]` is half a row's height, chosen so a group break reads as lighter than a row gap.
 */
export const menuSeparatorRecipe = cva([
  '-mx-[12px] my-[8px] h-0 shrink-0',
  'border-0 border-t border-solid border-[var(--ds-menu-separator)]',
]);
